// Azure/Microsoft OAuth Service using MSAL
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import api from './api';

const AZURE_CLIENT_ID = import.meta.env.VITE_AZURE_CLIENT_ID || '';
const AZURE_TENANT_ID = import.meta.env.VITE_AZURE_TENANT_ID || 'common';

class AzureAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private initialized = false;
  private initializing = false;

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializing) {
      // Wait for existing initialization
      while (this.initializing) {
        await new Promise(r => setTimeout(r, 50));
      }
      return;
    }

    this.initializing = true;
    try {
      this.msalInstance = new PublicClientApplication({
        auth: {
          clientId: AZURE_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${AZURE_TENANT_ID}`,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        }
      });

      await this.msalInstance.initialize();
      await this.msalInstance.handleRedirectPromise();
      this.initialized = true;
    } catch (error) {
      console.error('MSAL initialization error:', error);
      throw error;
    } finally {
      this.initializing = false;
    }
  }

  isConfigured(): boolean {
    return !!AZURE_CLIENT_ID;
  }

  async loginWithMicrosoft(): Promise<{ token: string; user: any }> {
    if (!this.isConfigured()) {
      throw new Error('Microsoft login is not configured. Contact your administrator.');
    }

    await this.initialize();

    if (!this.msalInstance) {
      throw new Error('MSAL not initialized. Please try again.');
    }

    // Cancel any existing interaction
    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
      }
    } catch {
      // ignore
    }

    console.log('[AzureAuth] Starting MSAL popup login...');

    let response;
    try {
      response = await this.msalInstance.loginPopup({
        scopes: ['User.Read', 'profile', 'openid', 'email'],
        prompt: 'select_account',
      });
    } catch (popupError: any) {
      console.error('[AzureAuth] MSAL popup error:', popupError);
      if (popupError.errorCode === 'user_cancelled') {
        throw new Error('Login was cancelled');
      }
      if (popupError.errorCode === 'popup_window_error') {
        throw new Error('Popup was blocked. Please allow popups for this site.');
      }
      throw new Error(`Microsoft login failed: ${popupError.message || 'Unknown error'}`);
    }

    if (!response || !response.accessToken) {
      throw new Error('No access token received from Microsoft');
    }

    console.log('[AzureAuth] Got access token, sending to backend...');

    // Send the access token to our backend with timeout
    const result = await api.post<{ success: boolean; token: string; user: any }>('/auth/azure/login', {
      accessToken: response.accessToken,
    });

    if (!result.success || !result.data) {
      console.error('[AzureAuth] Backend auth failed:', result.error);
      throw new Error(result.error || 'Microsoft authentication failed on the server');
    }

    console.log('[AzureAuth] Login successful');

    // Store the JWT from our backend
    api.setToken(result.data.token);

    return {
      token: result.data.token,
      user: result.data.user,
    };
  }

  async logout(): Promise<void> {
    if (this.msalInstance) {
      try {
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await this.msalInstance.logoutPopup({ account: accounts[0] });
        }
      } catch {
        // Ignore MSAL logout errors
      }
    }
  }
}

export const azureAuthService = new AzureAuthService();
export default azureAuthService;
